import axios from "axios"
import { chargebeeCreate, chargebeeUpdate } from "@/chargebee.js"
import { toast } from "@/toast"
import { queryClient } from "@/query.js"

const path = ext => ext ? `/customer/subscriptions/${ext}` : "/customer/subscriptions"
const createTrialPath = "/customer/subscriptions/trial"
const checkStackStatusCreateOrUpdate = function (stackId) {
  return axios.post(`/customer/stacks/${stackId}/status`, { accepted_states: ["CREATE_COMPLETE", "UPDATE_COMPLETE"] })
}
const checkInstanceStatusRunning = function (stackId) {
  return axios.post(`/customer/instances/${stackId}/status`, { accepted_states: ["RUNNING"] })
}

// I have by design not await this function later because I don't want to catch possible errors and show
// them as a toast.
const thirdPartyTrackingApi = config => import.meta.env.VITE_ENABLE_TRACKING === "true" && config.params.sub_id
  ? axios
      .create()
      .get("https://us-central1-big-query-adwords-cockpit.cloudfunctions.net/pairFpsIdSubId", config)
  : () => {}

// Stack statuses that represent a final state — polling must stop when any of these is reached.
//   ready                  — instance is live
//   failed                 — provisioning failed
//   blocked_sanction_review — compliance hold, CS must review before provisioning can proceed
const TERMINAL_STACK_STATUSES = new Set(["ready", "failed", "blocked_sanction_review"])

const subscription = {
  namespaced: true,
  state: {
    subscriptions: [],
    polls: new Set(),
    isCached: false,
    retryCount: 0,
  },
  mutations: {
    ADD_POLL(state, id) {
      state.polls.add(id)
    },
    CLEAR_POLL(state, { id }) {
      state.polls.delete(id)
    },
    CLEAR_CACHE(state) {
      state.isCached = false
    },
    SET_SUBSCRIPTIONS(state, subscriptions) {
      state.subscriptions = subscriptions
      state.isCached = true
    },
    ADD_SUBSCRIPTION(state, subscription) {
      state.subscriptions = [...state.subscriptions, subscription]
    },
    UPDATE_SUBSCRIPTION(state, subscription) {
      state.subscriptions = state.subscriptions.map(sub => sub.id === subscription.id ? subscription : sub)
    },
    REMOVE_SUBSCRIPTION(state, { id }) {
      state.subscriptions = state.subscriptions.filter(sub => sub.id !== id)
    },
    INCREMENT_RETRY_COUNT(state) {
      state.retryCount++
    },
    CLEAR_RETRY_COUNT(state) {
      state.retryCount = 0
    },
  },

  actions: {
    pollSubscription({ state, commit }, { id }) {
      if (state.polls.has(id)) {
        return
      }

      const poll = async () => {
        try {
          const { data } = await axios.get(path(id))
          const subscription = state.subscriptions.find(subscription => subscription.id === id)

          // Invalidate permissions cache only when transitioning specifically to "ready" —
          // the other terminal states (failed, blocked_sanction_review) do not provision
          // an instance, so no permission refresh is needed.
          if (subscription.stackStatus !== "ready" && data.stackStatus === "ready") {
            await queryClient.invalidateQueries({ queryKey: ["fetchAllPermissions"], refetchType: "active" })
          }

          commit("UPDATE_SUBSCRIPTION", data)

          if (TERMINAL_STACK_STATUSES.has(data.stackStatus)) {
            // Terminal state reached — stop polling regardless of which one it is.
            commit("CLEAR_POLL", data)
            commit("CLEAR_RETRY_COUNT")
          }
          else {
            setTimeout(poll, 5000)
            commit("ADD_POLL", id)
          }
        }
        catch (e) {
          if (e.response && e.response.status === 403 && state.retryCount < 10) {
            commit("INCREMENT_RETRY_COUNT")
            setTimeout(poll, 5000)
            commit("ADD_POLL", id)
          }
          else {
            commit("CLEAR_POLL", { id })
            commit("CLEAR_RETRY_COUNT")
            toast.error(e)
          }
        }
      }

      return poll()
    },
    async fetchSubscriptions({ state, commit }) {
      if (state.isCached) {
        return
      }

      try {
        const { data } = await axios.get(path())
        commit("SET_SUBSCRIPTIONS", data)

        thirdPartyTrackingApi({
          params: {
            sub_id: data.map(s => s.id).join(","),
            action: "fetch",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    async createSubscription({ commit }, { subdomain, id, name, region, tester }) {
      // Returns the created subscription on success (callers inspect stackStatus,
      // e.g. CSubscription.vue checks for "blocked_sanction_review") or null on failure.
      try {
        const { data } = await chargebeeCreate(subdomain, id, region, tester)
        commit("ADD_SUBSCRIPTION", data)
        toast.success(`Subscription ${name} created`)
        // https://jira.paesslergmbh.de/browse/PPHM-529:
        // Chargebee doesn't send this wanted information automatically in their integration
        window.dataLayer?.push({ event: "subscription", subscriptionId: data.id })
        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "create",
            date: Date.now(),
          },
        })
        return data
      }
      catch (e) {
        toast.error(e)
        return null
      }
    },
    async createTrial({ commit }, { subdomain, planId, companyName, country, region, shareUsageData }) {
      try {
        const { data } = await axios.post(createTrialPath, { subdomain, planId, companyName, country, region, shareUsageData })
        commit("ADD_SUBSCRIPTION", data.subscription)
        toast.success("Subscription trial created")
        window.dataLayer?.push({
          event: "cb_event",
          cbAction: "lead",
          cbCategory: "engagement",
        })
        window.dataLayer?.push({ event: "subscription", subscriptionId: data.subscription.id })
        thirdPartyTrackingApi({
          params: {
            sub_id: data.subscription.id,
            action: "create",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async updateSubscription({ commit, dispatch }, { id, planId, name, stackId }) {
      try {
        await Promise.all([
          checkInstanceStatusRunning(stackId),
          checkStackStatusCreateOrUpdate(stackId),
        ])
        const { data } = await chargebeeUpdate(id, planId)
        commit("UPDATE_SUBSCRIPTION", data)
        dispatch("pollSubscription", { id })
        toast.success(`Subscription updated to ${name}`)

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "update",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    async cancelSubscription({ commit }, { id, cancelReasonCode }) {
      // the returned booleans are needed later to determine if to route back to '/' or not
      try {
        const { data } = await axios.delete(path(id) + `?subscription_cancel_reason_code=${cancelReasonCode}`)
        if (data.subscriptionStatus === "cancelled") {
          commit("REMOVE_SUBSCRIPTION", data)
          commit("instance/REMOVE_TIMEZONE", data, { root: true })
        }
        else {
          commit("UPDATE_SUBSCRIPTION", data)
        }
        toast.success("Subscription cancelled")

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "cancel",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async restoreSubscription({ commit }, { id }) {
      try {
        await axios.post(path(`${id}/reactivate`))
        const { data } = await axios.get(path(id))
        commit("UPDATE_SUBSCRIPTION", data)
        toast.success("Subscription has been reactivated")

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "update",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    async changeSubdomain({ commit, dispatch }, { id, subdomain, stackId }) {
      try {
        await Promise.all([
          checkInstanceStatusRunning(stackId),
          checkStackStatusCreateOrUpdate(stackId),
        ])
        const { data } = await axios.put(path(`${id}/subdomain`), { subdomain })
        commit("UPDATE_SUBSCRIPTION", data)
        dispatch("pollSubscription", data)
      }
      catch (e) {
        toast.clear() // validateDomain() will trigger a toast, so without clearing it, the error will not be shown
        toast.error(e)
      }
    },
    checkIfExists({ state }, id) {
      if (state.subscriptions.some(sub => sub.id === id)) {
        return Promise.resolve(`Subscription ${id} already stored.`)
      }
      return axios.get(path(id))
    },
  },
}

export {
  subscription,
}
